// src/components/auth/ProtectedRoute.tsx

import { Navigate } from "@/routing/accountRouting";
import { useAuth } from "@/context/AuthContext";
import type { ComponentType, ReactNode } from "react";
import type { AccountRole, WorkspaceRole } from "@/types/auth";
import AccessDenied from "@/components/auth/AccessDenied";

/**
 * A section's chrome (sidebar + content area) with the content passed in.
 *
 * Section chrome is not a layout route here: every page renders its own
 * (`AppLayout sidebar={<SettingsSidebar />}` and friends). A denial that skips
 * the page therefore skips its menu too, unless the guard draws the denial
 * inside the same chrome — which is what `frame` is for.
 */
export type SectionFrame = ComponentType<{ children: ReactNode }>;

type BaseProps = {
  children: ReactNode;

  // Only superadmins may access
  requireSuperadmin?: boolean;

  // Superadmin OR white-label partner-admin (the scoped Clients view). The BFF
  // scopes the data to the partner-admin's own partner — this only gates access.
  requireSuperadminOrPartnerAdmin?: boolean;
};

// A role-gated route is reachable from a tenant menu, so its denial must keep
// that menu: `frame` is REQUIRED as soon as `accountRoles` or `workspaceRoles`
// is set. Superadmin-only routes are not in any tenant menu and may omit it.
type RoleProps =
  | { accountRoles?: undefined; workspaceRoles?: undefined; frame?: SectionFrame }
  | {
      // Account-level authorization (based on activeAccount.role)
      accountRoles: AccountRole[];
      workspaceRoles?: WorkspaceRole[];
      frame: SectionFrame;
    }
  | {
      accountRoles?: AccountRole[];
      // Workspace-level authorization (based on selected/ default workspace role)
      workspaceRoles: WorkspaceRole[];
      frame: SectionFrame;
    };

type ProtectedRouteProps = BaseProps & RoleProps;

const ROLE_AUDIENCE =
  "This page is available to account owners and admins. Ask one of them if you need access.";
const MEMBER_AUDIENCE =
  "This page is available to account owners, admins and members. Ask an account owner or admin if you need access.";

/** The denial names who the page IS for: a route that admits MEMBERs (the
 *  Analyst, plan 2026-10-24 § D8) must not tell a VIEWER it is admin-only. */
function accountAudience(accountRoles: AccountRole[]): string {
  return accountRoles.includes("MEMBER") ? MEMBER_AUDIENCE : ROLE_AUDIENCE;
}
const PLATFORM_AUDIENCE = "This page is only available to platform administrators.";
const NO_WORKSPACE_AUDIENCE =
  "You are not a member of any workspace in this account. Ask an account owner or admin to add you.";

export default function ProtectedRoute({
  children,
  accountRoles,
  workspaceRoles,
  frame: Frame,
  requireSuperadmin,
  requireSuperadminOrPartnerAdmin,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();

  const denied = (audience: string) => {
    const message = <AccessDenied audience={audience} />;
    return Frame ? <Frame>{message}</Frame> : message;
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  // 1) Superadmin-only route
  if (requireSuperadmin && !user.isSuperadmin) {
    return denied(PLATFORM_AUDIENCE);
  }

  // 1b) Superadmin OR partner-admin route
  if (
    requireSuperadminOrPartnerAdmin &&
    !user.isSuperadmin &&
    !user.isPartnerAdmin
  ) {
    return denied(PLATFORM_AUDIENCE);
  }

  // Superadmin bypass for role checks
  if (user.isSuperadmin) return <>{children}</>;

  // 2) Account role check
  if (accountRoles && accountRoles.length > 0) {
    const role = user.activeAccount?.role;
    if (!role || !accountRoles.includes(role)) {
      return denied(accountAudience(accountRoles));
    }
  }

  // 3) Workspace role check (use default workspace)
  if (workspaceRoles && workspaceRoles.length > 0) {
    const wsId = user.defaultWorkspaceId;
    const ws =
      user.workspaces?.find((w) => w.id === wsId) ?? user.workspaces?.[0];

    if (!ws) return denied(NO_WORKSPACE_AUDIENCE);
    // A workspace row without a role is not a member: deny, never assume.
    if (!ws.role || !workspaceRoles.includes(ws.role)) {
      return denied(ROLE_AUDIENCE);
    }
  }

  return <>{children}</>;
}
